import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { isMobile } from 'react-device-detect';
import useForceUpdate from 'use-force-update';
import CurrencyInput from '../elements/CurrencyInput';
import MoneyComponent from '../elements/MoneyComponent';
import AddCategoryForm from './AddCategoryForm';
import Category from '../../models/Category';
import { JqueryClassFaker } from './JqueryClassFaker';
import TransactionSummaryComponent from './TransactionSummaryComponent';
import Transaction from '../../models/Transaction';
import BudgetListModes from './BudgetListModes';
import TextInput from '../elements/TextInput';
import InlineProgressBar from "../../../../models/components/InlineProgressBar";
import { Provider, useSelector } from "react-redux";
import store from '../../redux/store/store.js';
import { useDispatch } from 'react-redux';
import { refreshDataAddToCacheOnly, unHighlightCategory, highlightCategory, setCategoryIsLoan } from "redux/actions/dashboard";
import { setTransactionToEditMode } from "redux/actions/dashboard";
import CategorySummaryNameDisplay from "./categorySummary/CategorySummaryNameDisplay.jsx";
import EditBudgetedAmountInput from "./categorySummary/EditBudgetAmountInput.jsx";
import CategorySummaryOuterContainer from "./categorySummary/CategorySummaryOuterContainer.jsx";
import CategorySummaryTransactions from "./categorySummary/CategorySummaryTransactions.jsx";
import MaterialIcon from "react/elements/MaterialIcon";
import categorySummaryCalculations from "utilities/budgetCalculations/categorySummaryCalculations";
import CategoryNameComponent from "react/components/CategoryNameComponent";
import RolloverIcon from "react/components/icons/RolloverIcon";
import { moneyFormatFromCents } from "utilities/moneyFormat";
import SelectedDates from "utilities/dates/SelectedDates";

export default function CategorySummaryComponent(props) {
	const {
		category,
		selectedDates,
		selectedDate,
		dragHandleProps,
		submittingBudgets,
		categoryGroup,
		sizeMobile,
		budgetMayHaveUpdated,
		editBudget,
	} = props;
	let {
		editBudgetListMode
	} = props;

	const [editing, updateEditing] = useState(false);
	const [editAsLoan, updateEditAsLoan] = useState(category.isLoanCategory(categoryGroup));
	const budgetInputRef = useRef(null);
	const forceUpdate = useForceUpdate();
	const setCategoryAsSubgroupCategory = useSelector((state) => state.dashboard.setCategoryAsSubgroupCategory);
	const highlightedCategory = useSelector(state => state.dashboard.highlightedCategory);

	const dispatch = useDispatch();


	const isEditingAllCategoryBudgets = editBudgetListMode === BudgetListModes.AMOUNTS;

	const isExpense = categoryGroup && categoryGroup.type === Category.types.EXPENSE;
	const isIncome = categoryGroup && categoryGroup.type === Category.types.INCOME;
	const isLoan = (categoryGroup && categoryGroup.type === Category.types.LOANS) || editAsLoan;
	const isSavings = categoryGroup && categoryGroup.type === Category.types.SAVINGS;
	const canHaveFund = isLoan || isSavings;
	const isSubmittingBudgetAmountChange = !!submittingBudgets[category.id];
	const isHighlighted = highlightedCategory && highlightedCategory === category.id || isEditingAllCategoryBudgets;

	const budgetAmountIsBeingEdited = editing || isEditingAllCategoryBudgets;

	useEffect(focusBudgetInputIfNecessary, [isHighlighted, isEditingAllCategoryBudgets, isMobile]);

	function focusBudgetInputIfNecessary() {
		if(isHighlighted && budgetInputRef.current && !isMobile && !isEditingAllCategoryBudgets) {
			budgetInputRef.current.focus();
		}
	}

	const { lastKnownInterest, lastKnownMinimumPayment, lastKnownBalance } = useMemo(retrieveLoanInformation, [category]);

	function retrieveLoanInformation() {

		return {
			lastKnownInterest: category.lastKnownInterestAmountBeforeDate(selectedDate),
			lastKnownMinimumPayment: category.lastKnownMinimumPaymentBeforeDate(selectedDate),
			lastKnownBalance: category.lastKnownBalanceBeforeDate(selectedDate),
		};
	}

	const setMinimumPaymentToLastKnown = () => {
		_updateBudgetAmountField('minimum_payment', lastKnownMinimumPayment);
	};
	const setBalanceToLastKnown = () => {
		_updateBudgetAmountField('fund_total', lastKnownBalance);

	};
	const budget = editBudget;

	const normalClickWillToggleDisplay = editBudgetListMode !== BudgetListModes.TYPES && !budgetAmountIsBeingEdited && !isHighlighted;
	const editingTypes = editBudgetListMode === BudgetListModes.TYPES;
	const showingSummaryInformation = (!budgetAmountIsBeingEdited && !isHighlighted) || (budget.isMultiBudget);

	const showingDetailedSummaryInformation = isHighlighted && !isEditingAllCategoryBudgets;

	function changedEditAsLoan(event) {
		const isLoan = event.target.checked;
		updateEditAsLoan(isLoan);
		dispatch(setCategoryIsLoan(category, isLoan))
			.then(() => updateEditAsLoan(category.isLoanCategory(categoryGroup)));
	}

	function categoryNameClicked() {
		if(showingDetailedSummaryInformation) {
			endHighlightCategory();
		}
	}

	const startAddTransaction = useCallback(() => {
		const { selectedDate, category } = props;

		const newTransaction = Transaction.create({
			where: '',
			amount: 0,
			transaction_date: selectedDate.unix(),
			category_id: category.id
		});
		console.log('triggered startAddTransaction', category, newTransaction, budget);
		store.dispatch(setTransactionToEditMode(newTransaction, { addingForSpecificCategory: true }));
	}, [selectedDate, category]);

	const startHighlightCategory = useCallback(() => {
		const { category } = props;
		dispatch(highlightCategory(category));
	}, [category]);

	const endHighlightCategory = useCallback(() => {
		dispatch(unHighlightCategory(category));
	}, []);

	const categorySubmit = useCallback((category, event) => {
		const { categorySubmit } = props;
		return categorySubmit(category, event)
			.then(() => {
				updateEditing(false);
			});
	}, [category]);

	function updateRolloverStart(event) {
		_updateBudgetAmountField(event.target.name, event.target.checked);
		editBudgetAmountBlur();
	}

	function updateRolloverEnd(event) {
		_updateBudgetAmountField(event.target.name, event.target.checked);
		editBudgetAmountBlur();
	}

	const _updateBudgetAmountField = useCallback((field, amount) => {
		budget.update(
			{
				[field]: amount
			}
		);
		budgetMayHaveUpdated();
		forceUpdate();
		// console.error('may need force update here');
		// this.forceUpdate();
	}, [category, budget]);

	const budgetEdit = useCallback((amount) => {
		budgetMayHaveUpdated();
		_updateBudgetAmountField('amount', amount);
	}, [category, budget]);

	const minimumPaymentEdit = useCallback((amount) => {
		_updateBudgetAmountField('minimum_payment', amount);
	}, [category, budget]);

	const fundEdit = useCallback((amount) => {
		_updateBudgetAmountField('fund_total', amount);
	}, [category, budget]);

	const editBudgetAmountBlur = () => {
		const { submitBudgetUpdate, category } = props;
		if(!budget.hasChanges()) {
			return;
		}
		submitBudgetUpdate(category, budget)
			.then(() => {
				dispatch(refreshDataAddToCacheOnly());
			});
	};

	const budgetFieldEdit = useCallback((event) => {
		_updateBudgetAmountField(event.target.name, event.target.value);
	}, [category, budget]);

	const budgetItemKeyPress = useCallback((event) => {
		if(event.key !== 'Enter') {
			return;
		}
		submitBudgetUpdate();
	}, [category, budget]);

	const submitBudgetUpdate = useCallback(() => {
		const { submitBudgetUpdate, category } = props;
		console.log('teh budget', budget);
		submitBudgetUpdate(category, budget)
			.then(endHighlightCategory)
			.then(() => {
				dispatch(refreshDataAddToCacheOnly());
			});
	}, [category, budget]);

	const classes = useMemo(() => {
		const classes = new JqueryClassFaker();

		if(category.parent_category_id) {
			classes.addClass('child_category');
		}
		if(!isHighlighted && normalClickWillToggleDisplay) {
			classes.addClass('clickable');
			classes.addClass('collapsed');
		}
		classes.toggleClass('highlighted', isHighlighted);
		return classes;
	});

	const {
		budgetSpent,
		budgetAvailable,
		totalBudgeted,
		displayedTotalBudgeted,
		displayedBudgetSpent,
		isRolloverRemainingAmount,
		canStartRolloverBudget,
		canEndRolloverBudget,
		rolloverBudgets,
		rolloverAmount,
	} = categorySummaryCalculations.getCategorySummaryCalculatedValues(budget, selectedDates, category);

	const copyBudgets = useMemo(() => {
		const copyBudgets = category.getUniqueBudgetsInReverseChronologicalOrder();

		if(selectedDate && budgetSpent) {
			const currentBudgetTotal = budget.clone();
			currentBudgetTotal.name = "Current Spent - " + moneyFormatFromCents(budgetSpent);
			currentBudgetTotal.amount = budgetSpent;
			currentBudgetTotal.id = "current_amount";
			copyBudgets.unshift(currentBudgetTotal);
		}

		return copyBudgets;
	}, [selectedDates, selectedDate, budget, category, budgetSpent]);

	const usePreviousAmountValue = useCallback((event) => {
		const { category, submitBudgetUpdate } = props;
		const budgetIndex = event.target.value;
		const copyBudget = copyBudgets[budgetIndex];

		if(!copyBudget) {
			return;
		}
		budgetEdit(copyBudget.amount);
		minimumPaymentEdit(copyBudget.minimum_payment);
		fundEdit(copyBudget.fund_total);
		_updateBudgetAmountField('interest_amount', copyBudget.interest_amount);
		submitBudgetUpdate(category, budget)
			.then(() => {
				dispatch(refreshDataAddToCacheOnly());
			});
	}, [copyBudgets, category, budget]);

	let displaySpecialForRemaining = null;
	let displaySpecialForBudgeted = null;
	let remainingExtraDisplay = null;
	let budgetedExtraDisplay = null;
	const masterParentArrow = <span title="Uses Parent Category's Budget" className='money neutral'><MaterialIcon>arrow_upward</MaterialIcon></span>;
	if(displayedTotalBudgeted === 0 && displayedBudgetSpent === 0 && budgetAvailable === 0) {
		displaySpecialForRemaining = (
			<span className='money_display money neutral'>---</span>
		);
	}
	if(category.is_master_budget_for_children) {
		budgetedExtraDisplay = <span title="Budget is used by child categories" className='money neutral'><MaterialIcon>arrow_downward</MaterialIcon></span>;
	}
	if(category.hasMasterBudgetParent()) {
		remainingExtraDisplay = masterParentArrow;
		budgetedExtraDisplay = masterParentArrow;
	}

	const categorySummaryOuterContainerProps = {
		normalClickWillToggleDisplay,
		startHighlightCategory,
		classes,
		setCategoryAsSubgroupCategory,
		category,
		...props,
	};

	const editBudgetAmountProps = {
		budget,
		rolloverBudgets,
		rolloverAmount,
		budgetAmount: budget.amount,
		budgetEdit,
		budgetItemKeyPress,
		isSubmittingBudgetAmountChange,
		copyBudgets,
		categoryGroup,
		usePreviousAmountValue,
		budgetInputRef,
		budgetSpent,
		budgetAvailable,
		onBlur: editBudgetAmountBlur,
	};

	const shouldShowEditBudgetInput = !budget.isMultiBudget && isHighlighted;
	const categoryBudgetChangeCanBeSubmitted = shouldShowEditBudgetInput;

	const showDragHandle = editingTypes || setCategoryAsSubgroupCategory;
	const shouldShowTransactionList = (budget.isMultiBudget && isHighlighted) || (!budget.isMultiBudget && !isEditingAllCategoryBudgets);

	const budgetTransactions = useMemo(() => {
		return budget.getTransactions();
	}, [budget, selectedDates]);

	const hasChanges = budget.hasChanges() || category.hasChanges();

	const categoryNameDisplayAndSuch = (
		<CategorySummaryNameDisplay
			showDragHandle={showDragHandle}
			dragHandleProps={dragHandleProps}
			editingTypes={editingTypes}
			showingDetailedSummaryInformation={showingDetailedSummaryInformation}
			budget={budget}
			highlighted={isHighlighted}
			onClick={categoryNameClicked}
			category={category}
			categoryBudgetChangeCanBeSubmitted={categoryBudgetChangeCanBeSubmitted}
			submittingBudgetAmountChange={isSubmittingBudgetAmountChange}
			hasChanges={hasChanges}
		/>
	);

	const categorySummaryTransactions = <CategorySummaryTransactions
		loan={canHaveFund}
		budget={budget}
		budgetTransactions={budgetTransactions}
		shouldShowTransactionList={shouldShowTransactionList}
		startAddTransaction={startAddTransaction}
		isHighlighted={isHighlighted}
		renderTransactionSummary={(transaction) => (
			<TransactionSummaryComponent
				key={transaction.id}
				transaction={transaction}
				viewOnly={false}
				{...props}
			/>
		)} />;

	if(editingTypes) {
		return (
			<CategorySummaryOuterContainer
				{...categorySummaryOuterContainerProps}
			>
				{categoryNameDisplayAndSuch}
				<div className='add_category_form col offset-s1 s10'>
					<Provider store={store}>
						<AddCategoryForm
							{...props}
							categorySubmit={categorySubmit}
						/>
					</Provider>
				</div>
			</CategorySummaryOuterContainer>
		);
	} else if(showingSummaryInformation) {
		return (
			<CategorySummaryOuterContainer
				{...categorySummaryOuterContainerProps}
				main={
					<React.Fragment>
						{categoryNameDisplayAndSuch}
						<React.Fragment>
							<div title='Budgeted'
							     className={`${shouldShowEditBudgetInput ? 'input-field' : ''} col s3`}>
								{shouldShowEditBudgetInput &&
								<EditBudgetedAmountInput
									{...editBudgetAmountProps}
								/>
								}
								{!shouldShowEditBudgetInput &&
								(
									<React.Fragment>
										{isRolloverRemainingAmount &&
										<RolloverIcon/>
										}
										{budgetedExtraDisplay}
										<MoneyComponent amount={totalBudgeted} />
									</React.Fragment>
								)
								}
							</div>
							{(!categoryGroup || isExpense) &&
							<div className={`budget_available ${sizeMobile ? 'mobile_padding' : ''} col s3`}>
								{isRolloverRemainingAmount &&
									<RolloverIcon/>
								}
								{displaySpecialForRemaining ||
								(
									<React.Fragment>
										<MoneyComponent
											showPositive={budgetAvailable >= 0}
											showNegative={budgetAvailable < 0}
											amount={budgetAvailable}
										>
										{remainingExtraDisplay}
										</MoneyComponent>
									</React.Fragment>
								)
								}
								{displayedTotalBudgeted > 0 &&
								<InlineProgressBar
									displayPercent={!sizeMobile}
									vertical={sizeMobile}
									total={displayedTotalBudgeted}
									current={displayedBudgetSpent}
									onHoverShow={<span>To Go</span>}
								/>
								}
								{displayedTotalBudgeted === 0 && displayedBudgetSpent > 0 &&
								<InlineProgressBar
									displayPercent={false}
									vertical={sizeMobile}
									total={100}
									current={101}
									onHoverShow={<span>To Go</span>}
								/>
								}
							</div>
							}
							{(isIncome || isSavings) &&
							<div title='Remaining' className='col s3'>
								{displaySpecialForRemaining || <MoneyComponent
									showPositive={totalBudgeted < budgetSpent}
									showNegative={totalBudgeted >= budgetSpent}
									amount={budgetSpent} />
								}
							</div>
							}
							{canHaveFund && selectedDate &&
							<div title='Amount Remaining' className={'col s3'}>
								<MoneyComponent
									showNegative={true}
									amount={budget.fund_total} />
							</div>
							}
						</React.Fragment>
					</React.Fragment>
				}
				secondary={
					categorySummaryTransactions
				}
			/>
		);
	}

	/**
	 * Inputs and such
	 */
	return (
		<CategorySummaryOuterContainer
			{...categorySummaryOuterContainerProps}
			main={
				<React.Fragment>
					{categoryNameDisplayAndSuch}
					<React.Fragment>
						<div className='input-field col s12 m3'>
							<EditBudgetedAmountInput
								{...editBudgetAmountProps}
							/>
							{category.hasMasterBudgetParent() &&
							<div className='note input-field'>
								{masterParentArrow} - Uses{' '}
								<CategoryNameComponent
									category={category.getParent()}
								/>'s
								Budget Automatically
							</div>
							}
						</div>
						<div
							className={`input-field col m3 s9`}>
							<TextInput
								autoComplete='off'
								type='text'
								name='notes'
								value={budget.notes === null ? '' : budget.notes}
								onChange={budgetFieldEdit}
								onBlur={editBudgetAmountBlur}
								onKeyPress={budgetItemKeyPress}
								disabled={isSubmittingBudgetAmountChange}
								label={`${budget.date.format('MMMM')} Notes`}
							/>
							{isExpense &&
							<React.Fragment>
								<div className='input-field'>
									<div className='edit_loan_amount'>
										<label>
											<input
												onChange={changedEditAsLoan}
												checked={editAsLoan}
												type="checkbox"
											/>
											<span>Is a Loan?</span>
										</label>
									</div>
								</div>
								{canStartRolloverBudget &&
								<div className='input-field'>
									<div className='edit_loan_amount'>
										<label>
											<input
												name='rolloverStartField'
												value='1'
												onChange={updateRolloverStart}
												checked={budget.isRolloverStart()}
												type="checkbox"
											/>
											<span>Budget Rolls Over?</span>
										</label>
									</div>
								</div>
								}
								{canEndRolloverBudget &&
								<div className='input-field'>
									<div className='edit_loan_amount'>
										<label>
											<input
												name='rolloverEndField'
												value='1'
												onChange={updateRolloverEnd}
												checked={budget.isRolloverEnd()}
												type="checkbox"
											/>
											<span>End Rollover Here?</span>
										</label>
									</div>
								</div>
								}
							</React.Fragment>
							}
						</div>
						{false && isSubmittingBudgetAmountChange &&
						<div className='updating_budget progress'>
							<div className='indeterminate' />
						</div>
						}
					</React.Fragment>
				</React.Fragment>
			}
			secondary={
				<React.Fragment>
					{canHaveFund && !budget.isMultiBudget &&
					<div className='category_summary row'>
						<div className='input-field col offset-m3 m3 s5'>
							<CurrencyInput
								allowNegative={false}
								value={budget.fund_total}
								name='fund_total'
								onChange={fundEdit}
								onBlur={editBudgetAmountBlur}
								onKeyPress={budgetItemKeyPress}
								disabled={isSubmittingBudgetAmountChange}
								label={isLoan ? 'Remaining Balance' : 'Balance'}
								placeholder={lastKnownBalance}
								allowEmptyFormatting={false}
							/>
							{<div className='hint_text clickable'
							      onClick={setBalanceToLastKnown}
							>
								Last Balance{' '}
								<MoneyComponent
									amount={lastKnownBalance}
								/>
							</div>
							}
						</div>
						<div className='input-field col m3 s4'>
							<CurrencyInput
								allowNegative={false}
								value={budget.minimum_payment}
								name='minimum_payment'
								onChange={minimumPaymentEdit}
								onBlur={editBudgetAmountBlur}
								onKeyPress={budgetItemKeyPress}
								disabled={isSubmittingBudgetAmountChange}
								label='Minimum'
								placeholder={lastKnownMinimumPayment}
								allowEmptyFormatting={false}
							/>
							{<div className='hint_text clickable'
							      onClick={setMinimumPaymentToLastKnown}
							>
								Last Payment{' '}
								<MoneyComponent
									amount={lastKnownMinimumPayment}
								/>
							</div>
							}
						</div>
						<div className='input-field col m3 s3'>
							<TextInput
								type='text'
								value={budget.interest_amount === null ? '' : budget.interest_amount}
								name='interest_amount'
								onChange={budgetFieldEdit}
								onBlur={editBudgetAmountBlur}
								onKeyPress={budgetItemKeyPress}
								disabled={isSubmittingBudgetAmountChange}
								label='Interest'
								placeholder={lastKnownInterest}
								autoComplete='off'
							/>
						</div>
					</div>
					}
					{categorySummaryTransactions}
				</React.Fragment>
			}
		/>
	);
}